/**
 * Business logic constants only.
 */

import { FormElementKind } from "@/gql/graphql";

export const CALORIES_PER_CARBOHYDRATE_GRAM = 4;
export const CALORIES_PER_PROTEIN_GRAM = 4;
export const CALORIES_PER_FAT_GRAM = 9;

export const FOOD_EXCHANGE_MULTIPLIERS = {
  vegetable: {
    carbohydrates: 3,
    proteins: 1,
    fats: 0,
    calories: 16,
  },
  vegetables: {
    carbohydrates: 5,
    proteins: 2,
    fats: 0,
    calories: 25,
  },
  treats: {
    carbohydrates: 15,
    proteins: 0,
    fats: 5,
    calories: 105,
  },
  starches: {
    carbohydrates: 15,
    proteins: 3,
    fats: 1,
    calories: 80,
  },
  dairy: {
    carbohydrates: 12,
    proteins: 8,
    fats: 5,
    calories: 120,
  },
  meatAndSeafood: {
    carbohydrates: 0,
    proteins: 7,
    fats: 5,
    calories: 75,
  },
  fatsAndOil: {
    calories: 45,
    carbohydrates: 0,
    fats: 5,
    proteins: 0,
  },
  fruits: {
    calories: 60,
    carbohydrates: 15,
    fats: 0,
    proteins: 0,
  },
  plantBasedMeat: {
    calories: 87,
    carbohydrates: 8,
    fats: 3,
    proteins: 7,
  },
  fruit: {
    carbohydrates: 10,
    proteins: 0,
    fats: 0,
    calories: 40,
  },
  // TODO: delete starch
  starch: {
    carbohydrates: 23,
    proteins: 2,
    fats: 0,
    calories: 100,
  },
  fat: {
    carbohydrates: 0,
    proteins: 0,
    fats: 5,
    calories: 45,
  },
  sugar: {
    carbohydrates: 5,
    proteins: 0,
    fats: 0,
    calories: 20,
  },
  milk: {
    whole: {
      carbohydrates: 12,
      proteins: 8,
      fats: 10,
      calories: 170,
    },
    lowFat: {
      carbohydrates: 12,
      proteins: 8,
      fats: 5,
      calories: 125,
    },
    nonFatSkim: {
      carbohydrates: 12,
      proteins: 8,
      fats: 0,
      calories: 80,
    },
  },
  // non-nested milk for easier property access by FoodExchange store
  milkWhole: {
    carbohydrates: 12,
    proteins: 8,
    fats: 10,
    calories: 170,
  },
  milkLowFat: {
    carbohydrates: 12,
    proteins: 8,
    fats: 5,
    calories: 125,
  },
  milkNonFatSkim: {
    carbohydrates: 12,
    proteins: 8,
    fats: 0,
    calories: 80,
  },
  rice: {
    lowProtein: {
      carbohydrates: 23,
      proteins: 0,
      fats: 0,
      calories: 92,
    },
    mediumProtein: {
      carbohydrates: 23,
      proteins: 2,
      fats: 0,
      calories: 100,
    },
    highProtein: {
      carbohydrates: 23,
      proteins: 4,
      fats: 0,
      calories: 108,
    },
  },
  // non-nested rice for easier property access by FoodExchange store
  riceLowProtein: {
    carbohydrates: 23,
    proteins: 0,
    fats: 0,
    calories: 92,
  },
  riceMediumProtein: {
    carbohydrates: 23,
    proteins: 2,
    fats: 0,
    calories: 100,
  },
  riceHighProtein: {
    carbohydrates: 23,
    proteins: 4,
    fats: 0,
    calories: 108,
  },
  meat: {
    lowFat: {
      carbohydrates: 0,
      proteins: 8,
      fats: 1,
      calories: 41,
    },
    moderateFat: {
      carbohydrates: 0,
      proteins: 8,
      fats: 6,
      calories: 86,
    },
    highFat: {
      carbohydrates: 0,
      proteins: 8,
      fats: 10,
      calories: 122,
    },
  },
  // non-nested meat for easier property access by FoodExchange store
  meatLowFat: {
    carbohydrates: 0,
    proteins: 8,
    fats: 1,
    calories: 41,
  },
  meatModerateFat: {
    carbohydrates: 0,
    proteins: 8,
    fats: 6,
    calories: 86,
  },
  meatHighFat: {
    carbohydrates: 0,
    proteins: 8,
    fats: 10,
    calories: 122,
  },
};

export const FORM_ELEMENT_KINDS = [
  {
    singleFile: null,
    singleFileUrl: "",
    kind: FormElementKind.Section,
    required: false,
    sequence: 0,
    arguments: {
      buttonLabel: "",
    },
  },
  {
    singleFile: null,
    singleFileUrl: "",
    kind: FormElementKind.Header,
    required: false,
    sequence: 0,
    arguments: {
      label: "",
    },
  },
  {
    multipleFileUrls: [] as string[],
    kind: FormElementKind.Description,
    required: false,
    sequence: 0,
    arguments: {
      description: "",
    },
  },
  {
    kind: FormElementKind.LongText,
    required: false,
    sequence: 0,
    arguments: {
      label: "",
      defaultText: "",
    },
  },
  {
    kind: FormElementKind.ShortText,
    required: false,
    sequence: 0,
    arguments: {
      label: "",
      defaultText: "",
    },
  },
  {
    kind: FormElementKind.Dropdown,
    required: false,
    sequence: 0,
    arguments: {
      label: "",
      options: [],
    },
  },
  {
    kind: FormElementKind.Number,
    required: false,
    sequence: 0,
    arguments: {
      label: "",
      value: "",
    },
  },
  {
    kind: FormElementKind.VerticalSingleSelector,
    required: false,
    sequence: 0,
    arguments: {
      options: [],
    },
  },
  {
    kind: FormElementKind.VerticalMultipleSelector,
    required: false,
    sequence: 0,
    arguments: {
      label: "",
      options: [
        {
          value: "",
          checked: true,
        },
      ],
    },
  },
  {
    kind: FormElementKind.Date,
    required: false,
    sequence: 0,
    arguments: {
      label: "",
      dateFormat: "",
    },
  },
  {
    kind: FormElementKind.Media,
    required: false,
    sequence: 0,
    arguments: {
      label: "",
      maximumNumberOfImages: 2,
      maximumImageSize: 10,
    },
  },
  {
    kind: FormElementKind.MediaUrl,
    required: false,
    sequence: 0,
    arguments: {
      label: "",
      urlAddress: "",
    },
  },
  {
    kind: FormElementKind.File,
    required: false,
    sequence: 0,
    arguments: {
      label: "",
      maximumNumberOfFiles: 2,
      maximumFileSize: 10,
    },
  },
  {
    kind: FormElementKind.Checkbox,
    sequence: 0,
    required: false,
    arguments: {
      label: "",
      affirmation: "",
      checked: false,
    },
  },
  {
    kind: FormElementKind.Signature,
    sequence: 0,
    required: false,
    arguments: {
      label: "",
    },
  },
];
